// WishlistGrid.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaGift, FaTrash, FaEdit, FaSave, FaTimes, FaSpinner } from 'react-icons/fa';

const WishlistGrid = ({ id }) => {
  const [allItems, setAllItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [editItemId, setEditItemId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const itemsPerLoad = 12;
  const observer = useRef();

  useEffect(() => {
    fetchWishlist();
  }, [id]);

  const fetchWishlist = () => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/wishlists/${id}`)
      .then((res) => {
        const sorted = res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAllItems(sorted);
        setDisplayedItems(sorted.slice(0, itemsPerLoad));
        setHasMore(sorted.length > itemsPerLoad);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching wishlist:', err);
        setLoading(false);
      });
  };

  const loadMore = () => {
    const nextItems = allItems.slice(displayedItems.length, displayedItems.length + itemsPerLoad);
    setDisplayedItems(prev => [...prev, ...nextItems]);
    if (displayedItems.length + nextItems.length >= allItems.length) {
      setHasMore(false);
    }
  };

  const lastItemRef = useCallback(node => {
    if (loading || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, displayedItems]);

  const handleDelete = async (itemId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    setDeletingId(itemId);

    try {
      if (imageUrl && imageUrl.includes('/wp-content/uploads/')) {
        const fileName = imageUrl.split('/').pop();
        const mediaList = await axios.get(`${process.env.REACT_APP_PAY_BASE_URL}/wp-json/wp/v2/media?search=${fileName}`, {
          headers: {
            Authorization: 'Basic ' + btoa(`${process.env.REACT_APP_WP_USERNAME}:${process.env.REACT_APP_WP_APP_PASSWORD}`),
          },
        });
        const media = mediaList.data.find(m => m.source_url === imageUrl);
        if (media?.id) {
          await axios.delete(`${process.env.REACT_APP_PAY_BASE_URL}/wp-json/wp/v2/media/${media.id}?force=true`, {
            headers: {
              Authorization: 'Basic ' + btoa(`${process.env.REACT_APP_WP_USERNAME}:${process.env.REACT_APP_WP_APP_PASSWORD}`),
            },
          });
        }
      }

      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/wishlists/${itemId}`);
      fetchWishlist();
    } catch (err) {
      console.error('Error deleting item:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditClick = (item) => {
    setEditItemId(item.id);
    setEditTitle(item.title);
    setEditUrl(item.url || '');
  };

  const handleCancelEdit = () => {
    setEditItemId(null);
    setEditTitle('');
    setEditUrl('');
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/wishlists/${editItemId}`, {
        title: editTitle,
        url: editUrl,
      });
      handleCancelEdit();
      fetchWishlist();
    } catch (err) {
      console.error('Error updating wishlist item:', err);
    }
  };

  if (loading) {
    return <div className="text-center py-10 text-gray-500">Loading wishlist...</div>;
  }

  if (displayedItems.length === 0) {
    return (
      <div className="text-center py-20 text-gray-600">
        <FaGift className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-4 text-lg font-medium">No wishlist items added yet.</p>
        <p className="text-sm text-gray-500">Click "Add Wishlist" to add your first item.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {displayedItems.map((item, index) => (
        <div
          key={item.id}
          ref={index === displayedItems.length - 1 ? lastItemRef : null}
          className="border rounded p-4 shadow-sm hover:shadow-md transition relative"
        >
          {item.image_url && (
            <img
              src={item.image_url}
              alt={item.title}
              className="w-full aspect-square object-cover mb-2 rounded"
            />
          )}

          {editItemId === item.id ? (
            <>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                placeholder="Enter title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full border rounded px-2 py-1 mb-2 text-sm"
              />
              <label className="block text-sm font-medium text-gray-700">URL (optional)</label>
              <input
                type="url"
                placeholder="Enter URL"
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                className="w-full border rounded px-2 py-1 mb-2 text-sm"
              />
              <div className="flex space-x-2 text-sm">
                <button
                  onClick={handleSaveEdit}
                  className="text-green-600 hover:underline"
                >
                  <FaSave className="inline mr-1" /> Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-600 hover:underline"
                >
                  <FaTimes className="inline mr-1" /> Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-md mb-1 line-clamp-2">{item.title}</h3>
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm"
                >
                  View item
                </a>
              )}
              <div className="mt-3 flex space-x-2 text-sm items-center">
                <button
                  onClick={() => handleEditClick(item)}
                  className="text-yellow-600 hover:underline"
                >
                  <FaEdit className="inline mr-1" /> Edit
                </button>
                {deletingId === item.id ? (
                  <FaSpinner className="animate-spin text-gray-400 ml-2" />
                ) : (
                  <button
                    onClick={() => handleDelete(item.id, item.image_url)}
                    className="text-red-600 hover:underline"
                  >
                    <FaTrash className="inline mr-1" /> Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default WishlistGrid;
